import React, { useMemo, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  TooltipProps,
} from "recharts";
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";
import { CompetitiveIntensityData } from "../../../types/contractDashboard";
import styles from "./competitiveIntensity.module.scss";

interface CompetitiveIntensityProps {
  data: CompetitiveIntensityData[];
}

// Custom tooltip component for better UX
const CustomTooltip: React.FC<TooltipProps<ValueType, NameType>> = ({
  active,
  payload,
  label,
}) => {
  if (!active || !payload || !payload.length) {
    return null;
  }

  const value = payload[0].value as number;

  return (
    <Box
      sx={{
        backgroundColor: "rgba(255, 255, 255, 0.96)",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        padding: "12px",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
      }}
    >
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, marginBottom: "8px", color: "#1a1a1a" }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <Box
          sx={{
            width: "12px",
            height: "12px",
            borderRadius: "2px",
            backgroundColor: payload[0].color,
          }}
        />
        <Typography variant="body2" sx={{ color: "#666", fontSize: "13px" }}>
          Avg Bids:
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: "#1a1a1a", fontSize: "13px" }}
        >
          {value.toFixed(1)}
        </Typography>
      </Box>
    </Box>
  );
};

const CompetitiveIntensity: React.FC<CompetitiveIntensityProps> = ({
  data: propData,
}) => {
  // Memoize chart data transformation
  const chartData = useMemo(() => {
    return propData.map((item) => ({
      category: item.category,
      avgBids: item.avgBids,
    }));
  }, [propData]);

  // Memoize X-axis domain calculation
  const xAxisDomain = useMemo(() => {
    if (!chartData || chartData.length === 0) return [0, 8];
    const maxValue = Math.max(...chartData.map((d) => d.avgBids));
    return [0, Math.ceil(maxValue * 1.2)];
  }, [chartData]);

  // Memoize X-axis tick formatter
  const xAxisTickFormatter = useCallback((value: number) => {
    return value.toFixed(0);
  }, []);

  if (!propData || propData.length === 0) {
    return (
      <Box className={styles.container}>
        <Box className={styles.titleSection}>
          <Typography variant="h6" className={styles.title}>
            Competitive Intensity
          </Typography>
          <Typography variant="body2" className={styles.subtitle}>
            Average number of qualified bids per sourcing event.
          </Typography>
        </Box>
        <Box className={styles.chartWrapper}>
          <Typography
            variant="body2"
            style={{ textAlign: "center", padding: "20px" }}
          >
            No data available
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.titleSection}>
        <Typography variant="h6" className={styles.title}>
          Competitive Intensity
        </Typography>
        <Typography variant="body2" className={styles.subtitle}>
          Average number of qualified bids per sourcing event.
        </Typography>
      </Box>

      <Box className={styles.chartWrapper}>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 10, right: 30, left: 80, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
            <XAxis
              type="number"
              domain={xAxisDomain}
              tickFormatter={xAxisTickFormatter}
              tick={{ fontSize: 12, fill: "#6b7280" }}
              tickLine={{ stroke: "#e5e7eb" }}
              axisLine={{ stroke: "#e5e7eb" }}
            />
            <YAxis
              type="category"
              dataKey="category"
              tick={{ fontSize: 12, fill: "#6b7280" }}
              tickLine={{ stroke: "#e5e7eb" }}
              axisLine={{ stroke: "#e5e7eb" }}
              width={75}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
            />
            <Bar
              dataKey="avgBids"
              fill="#00d2aa"
              name="Average Bids"
              animationDuration={800}
              radius={[0, 4, 4, 0]}
              barSize={30}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default React.memo(CompetitiveIntensity);

